import {
  hasFetchedGlobalDataTypes,
  hasFetchedPropertyDataTypes,
  prefetchAllGlobalDataTypes,
  prefetchAllPropertyDataTypes,
  prefetchGlobalDataType,
  prefetchPropertyDataType,
  useGlobalDataTypeKey,
  usePropertyDataTypeKey,
} from "@src/data-hooks/data-types/dataTypes.service";
import { IRawDataType } from "@src/data-hooks/data-types/dataTypes.types";
import { queryClient } from "@src/index";
import { translate } from "@src/libs/i18next/translations.util";
import { LocalData } from "@src/to-be-removed/LocalData/General/LocalData";
import { getStringArray } from "@src/utils/arrayUtilities";
import { CustomFieldDataOptions } from "../../../../Property/Collections/PropertyCustomFieldData";
import { CustomFieldDataType } from "../../CustomFieldDataType";
import { CustomFieldDataTypeHelper, DataEditorContext } from "../CustomFieldDataTypeHelper";
import { CustomDataTypeEditor } from "./CustomDataTypeEditor";

export class CustomFieldCustomDataType extends CustomFieldDataTypeHelper<CustomFieldDataType | CustomFieldDataType[]> {
  getDataEditor(
    data: any | undefined,
    options: CustomFieldDataOptions,
    context: DataEditorContext,
    setRawData: (data: string | string[] | undefined) => void
  ): JSX.Element {
    return <CustomDataTypeEditor data={data} options={options} context={context} setRawData={setRawData} />;
  }

  override loadData(context: DataEditorContext, onFinish?: () => void) {
    const promises: Promise<any>[] = [];

    if (context.propertyID && !hasFetchedPropertyDataTypes(context.propertyID)) {
      promises.push(prefetchAllPropertyDataTypes(context.propertyID));
    }
    if (!hasFetchedGlobalDataTypes()) {
      promises.push(prefetchAllGlobalDataTypes(LocalData.currentUser.id));
    }
    if (promises.length > 0) {
      Promise.allSettled(promises).then(() => onFinish?.());
    }
  }

  getString(values: string[], options: IRawDataType["options"]) {
    return values.map((value) => translate(Object.entries(options).find(([id]) => id === value)?.[1].name)).join(", ");
  }

  // TODO: Later we should remake the CustomFieldDataTypeHelper to use hooks
  override getStringRepresentation(data: any | undefined, options: CustomFieldDataOptions, context: DataEditorContext): string | Promise<string> {
    if (context.propertyID && options.customDataTypeID) {
      const values = getStringArray(data);

      const dataType = this.getDataType(options.customDataTypeID, context);
      if (dataType) {
        return this.getString(values, dataType.options);
      } else {
        return this.loadDataType(options.customDataTypeID, context).then((result) => {
          return this.getString(values, result?.options ?? {});
        });
      }
    }
    return "";
  }

  // TODO: Later we should remake the CustomFieldDataTypeHelper to use hooks
  getDataType(customDataTypeID: string, context: DataEditorContext): IRawDataType | undefined {
    const queryKeys: any[] = [useGlobalDataTypeKey(customDataTypeID)];
    if (context.propertyID) queryKeys.push(usePropertyDataTypeKey(context.propertyID, customDataTypeID));

    for (const queryKey of queryKeys) {
      const dataType = queryClient.getQueryData<IRawDataType>(queryKey);
      if (dataType) return dataType;
    }
    return undefined;
  }

  async loadDataType(customDataTypeID: string, context: DataEditorContext): Promise<IRawDataType | undefined> {
    await Promise.allSettled([
      context.propertyID ? prefetchPropertyDataType(context.propertyID, customDataTypeID) : undefined,
      prefetchGlobalDataType(customDataTypeID),
    ]);
    return this.getDataType(customDataTypeID, context);
  }
}
